/**
 * Custom error class for application-specific errors
 */
export class ApplicationError extends Error {
  public readonly code?: string;
  public readonly details?: unknown;
  public readonly timestamp: number;

  constructor(message: string, code?: string, details?: unknown) {
    super(message);
    this.name = "ApplicationError";
    this.code = code;
    this.details = details;
    this.timestamp = Date.now();
  }

  toJSON() {
    return {
      message: this.message,
      code: this.code,
      details: this.details,
      timestamp: this.timestamp,
    };
  }
}

/**
 * Error codes for consistent error handling
 */
export const ErrorCodes = {
  // Variable errors
  VARIABLE_NOT_FOUND: "VARIABLE_NOT_FOUND",
  VARIABLE_NAME_CONFLICT: "VARIABLE_NAME_CONFLICT",
  VARIABLE_INVALID_NAME: "VARIABLE_INVALID_NAME",
  VARIABLE_UPDATE_FAILED: "VARIABLE_UPDATE_FAILED",

  // Editor errors
  EDITOR_NOT_INITIALIZED: "EDITOR_NOT_INITIALIZED",
  EDITOR_COMMAND_FAILED: "EDITOR_COMMAND_FAILED",
  EDITOR_CONTENT_INVALID: "EDITOR_CONTENT_INVALID",

  // Image errors
  IMAGE_INVALID_FORMAT: "IMAGE_INVALID_FORMAT",
} as const;

/**
 * Log error with consistent format
 */
export function logError(error: unknown, context?: string): void {
  console.error("[Application Error]", {
    timestamp: new Date().toISOString(),
    context,
    error,
  });
}

/**
 * Safely execute sync operations with error handling
 */
export function safeSync<T>(operation: () => T, fallback: T, context?: string): T {
  try {
    return operation();
  } catch (error) {
    logError(error, context);
    return fallback;
  }
}
